import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ButtonBase from '@mui/material/ButtonBase';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/constants/routes.constants';
import { useLogoutMutation } from '@/features/auth/services/auth.api';

function getInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const initials = parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase());
  return initials.join('') || '?';
}

export function AccountMenu() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [logout, { isLoading: isLoggingOut }] = useLogoutMutation();
  const { user, logout: clearSession } = useAuth();
  const navigate = useNavigate();

  const isMenuOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const goTo = (path: string) => {
    handleClose();
    navigate(path);
  };

  const handleLogoutConfirm = async () => {
    try {
      await logout().unwrap();
    } finally {
      clearSession();
      setIsLogoutDialogOpen(false);
      navigate(ROUTES.LOGIN, { replace: true });
    }
  };

  if (!user) return null;

  return (
    <>
      <ButtonBase
        onClick={handleOpen}
        aria-controls={isMenuOpen ? 'account-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={isMenuOpen ? 'true' : undefined}
        aria-label={t('layout.accountMenu')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          ml: 1,
          px: 1,
          py: 0.5,
          borderRadius: 2,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Avatar
          src={user.profile_image ?? undefined}
          sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14, fontWeight: 600 }}
        >
          {getInitials(user.full_name)}
        </Avatar>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, display: { xs: 'none', sm: 'block' } }}
          noWrap
        >
          {user.full_name}
        </Typography>
        <KeyboardArrowDownOutlinedIcon
          fontSize="small"
          sx={{ display: { xs: 'none', sm: 'block' }, color: 'text.secondary' }}
        />
      </ButtonBase>

      <Menu
        id="account-menu"
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 1 } } }}
      >
        <Stack sx={{ px: 2, py: 1.5 }} spacing={0.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
            {user.full_name}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {user.email}
          </Typography>
        </Stack>

        <Divider />

        <MenuItem onClick={() => goTo(ROUTES.PROFILE)}>
          <ListItemIcon>
            <PersonOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('account.profile')}</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => goTo(ROUTES.CHANGE_PASSWORD)}>
          <ListItemIcon>
            <LockOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('account.changePassword')}</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={() => {
            handleClose();
            setIsLogoutDialogOpen(true);
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{t('account.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={isLogoutDialogOpen}
        title={t('account.signOutTitle')}
        description={t('account.signOutDescription')}
        confirmLabel={t('account.signOutConfirm')}
        isLoading={isLoggingOut}
        onConfirm={handleLogoutConfirm}
        onCancel={() => setIsLogoutDialogOpen(false)}
      />
    </>
  );
}
